import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { ProductVariant } from '@/api/models';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';
import { ProductDetailField } from './ProductDetailField';
import {
    formatMoney,
    formatNumber,
    formatPercent,
    orDash,
    stockScopeCopy,
} from './productDetailMeta';

type ProductVariantFiguresProps = {
    variant: ProductVariant;
    /** Whose stock the figures count, which the cost and markup name. */
    stockFigures: StockFiguresScope;
};

/**
 * A variant's figures in its open panel (`ProductVariantPanel`): the sale
 * and wholesale prices, the minimum, the cost and markup (named for whose
 * stock they count, bos-app#119), weight, volume, the unique key and the
 * variation code, as a grid of label and value pairs.
 */
export const ProductVariantFigures: React.FC<ProductVariantFiguresProps> = ({
    variant,
    stockFigures,
}) => {
    const { t } = useTranslation('pages');
    const scope = stockScopeCopy(stockFigures);

    return (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
            <ProductDetailField
                isFigure
                label={t('products.detail.variants.salePrice')}
            >
                {formatMoney(variant.salePriceTetri)}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t('products.detail.variants.wholesalePrice')}
            >
                {formatMoney(variant.wholesalePriceTetri)}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t('products.detail.variants.minimum')}
            >
                {formatNumber(variant.minimumQuantity)}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t(scope.keys.cost, scope.values)}
            >
                {formatMoney(variant.currentCostTetri)}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t(scope.keys.markup, scope.values)}
            >
                {formatPercent(variant.markupPercent)}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t('products.detail.variants.weight')}
            >
                {formatNumber(variant.weight)}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t('products.detail.variants.volume')}
            >
                {formatNumber(variant.volume)}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t('products.detail.variants.uniqueKey')}
            >
                {variant.uniqueKey}
            </ProductDetailField>
            <ProductDetailField
                isFigure
                label={t('products.detail.variants.diff')}
            >
                {orDash(variant.diff)}
            </ProductDetailField>
        </dl>
    );
};
